import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { PremiumBadge } from '../../components/ui/premium-badge';
import { PremiumUpgradeDialog } from '../../components/ui/premium-overlay';
import { useToast } from '../../hooks/use-toast';
import { formatCurrency, formatDate } from '../../lib/i18n';
import {
  ArrowLeft,
  User,
  Wrench,
  Package,
  FileText,
  Clock,
  CheckCircle,
  Edit,
  Printer,
  AlertTriangle,
  Trash2,
  ChevronDown,
  MessageCircle,
  Download,
  Share2,
  AlertCircle,
  Settings2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  waiting_parts: 'bg-orange-100 text-orange-800 border-orange-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
  delivered: 'bg-slate-100 text-slate-800 border-slate-300'
};

const statusLabels = {
  en: {
    pending: 'Pending',
    in_progress: 'In Progress',
    waiting_parts: 'Waiting for Parts/Hold',
    completed: 'Completed',
    delivered: 'Delivered'
  },
  bn: {
    pending: 'অপেক্ষমান',
    in_progress: 'চলমান',
    waiting_parts: 'পার্টসের জন্য অপেক্ষা/হোল্ড',
    completed: 'সম্পন্ন',
    delivered: 'ডেলিভারি হয়েছে'
  }
};

const statusFlow = ['pending', 'in_progress', 'waiting_parts', 'completed', 'delivered'];

export default function JobDetailPage() {
  const { id, garageSlug } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, hasRole, isPremium } = useAuth();
  const { toast } = useToast();
  
  // Build base path for navigation
  const basePath = garageSlug ? `/${garageSlug}/admin` : '/admin';
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [hasInvoice, setHasInvoice] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingJob, setDeletingJob] = useState(false);
  const [quotationLoading, setQuotationLoading] = useState(false);

  // Thermal paper size
  const [thermalPaperSize, setThermalPaperSize] = useState(80);

  // Status change confirmation
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);

  // Premium upgrade popup
  const [upgradePopup, setUpgradePopup] = useState({ open: false, feature: '' });

  // Customer dues
  const [customerDues, setCustomerDues] = useState(null);

  useEffect(() => {
    fetchJob();
    // Fetch thermal paper size from garage settings
    axios.get(`${API}/settings/garage`).then(res => {
      setThermalPaperSize(res.data.thermal_paper_size || 80);
    }).catch(() => {});
  }, [id]);

  const fetchJob = async () => {
    try {
      const [jobRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/jobs/${id}`),
        axios.get(`${API}/invoices?job_id=${id}`).catch(() => ({ data: [] }))
      ]);
      setJob(jobRes.data);
      // Check if any invoice exists for this job
      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
      const jobInvoice = invoices.find(inv => inv.job_id === id);
      setHasInvoice(!!jobInvoice);
      setInvoiceId(jobInvoice?.id || null);
      // Fetch customer dues (non-blocking)
      if (jobRes.data?.customer_id) {
        axios.get(`${API}/invoices/dues/customer/${jobRes.data.customer_id}`)
          .then(r => setCustomerDues(r.data))
          .catch(() => setCustomerDues(null));
      }
    } catch (error) {
      console.error('Failed to fetch job:', error);
      toast({ title: 'Job not found', variant: 'destructive' });
      navigate(`${basePath}/jobs`);
    } finally {
      setLoading(false);
    }
  };

  const isStatusBackwards = (currentStatus, newStatus) => {
    const currentIndex = statusFlow.indexOf(currentStatus);
    const newIndex = statusFlow.indexOf(newStatus);
    return newIndex < currentIndex;
  };

  const handleStatusChange = (newStatus) => {
    if (isStatusBackwards(job.status, newStatus)) {
      setPendingStatus(newStatus);
      setShowStatusConfirm(true);
    } else {
      updateStatus(newStatus);
    }
  };

  const confirmStatusChange = () => {
    updateStatus(pendingStatus);
    setShowStatusConfirm(false);
    setPendingStatus(null);
  };

  const cancelStatusChange = () => {
    setShowStatusConfirm(false);
    setPendingStatus(null);
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await axios.put(`${API}/jobs/${id}/status?status=${newStatus}`);
      setJob(prev => ({ ...prev, status: newStatus }));
      toast({ title: language === 'en' ? 'Status updated!' : 'স্ট্যাটাস আপডেট হয়েছে!' });
    } catch (error) {
      toast({ title: 'Failed to update status', variant: 'destructive' });
    } finally {
      setUpdating(false);
    }
  };

  const createInvoice = async () => {
    navigate(`${basePath}/invoices/new?job_id=${id}`);
  };

  const deleteJob = async () => {
    setDeletingJob(true);
    try {
      await axios.delete(`${API}/jobs/${id}`);
      toast({ title: language === 'en' ? 'Job deleted' : 'জব মুছে ফেলা হয়েছে' });
      navigate(`${basePath}/jobs`);
    } catch (error) {
      toast({ title: error.response?.data?.detail || (language === 'en' ? 'Failed to delete job' : 'জব মুছতে ব্যর্থ'), variant: 'destructive' });
    } finally {
      setDeletingJob(false);
      setShowDeleteConfirm(false);
    }
  };

  const printQuotation = async () => {
    setQuotationLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${id}/quotation-pdf?paper_size=${thermalPaperSize}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
      toast({ title: language === 'en' ? 'Quotation ready' : 'কোটেশন প্রস্তুত' });
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to generate quotation' : 'কোটেশন তৈরি ব্যর্থ', variant: 'destructive' });
    } finally {
      setQuotationLoading(false);
    }
  };

  const shareQuotationWhatsApp = async () => {
    if (!job) return;
    setQuotationLoading(true);
    try {
      // Get share link + shop info from backend
      const shareRes = await axios.post(`${API}/jobs/${id}/quotation-share-link`);
      const { share_url: shareUrl, shop_name, shop_phone, shop_slug, frontend_url } = shareRes.data;
      
      const phone = (job.customer_phone || '').replace(/\D/g, '');
      const customerName = (job.customer_name || 'Customer').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      const bikeInfo = [job.bike_make, job.bike_model].filter(Boolean).join(' ');

      const lines = [
        `Dear ${customerName},`,
        '',
        `Your quotation *#${job.job_number}*${shop_name ? ` from *${shop_name}*` : ''} is ready.`,
        bikeInfo ? `Vehicle: *${bikeInfo}*` : '',
        `Estimated Total: *Tk ${(job.total_amount || 0).toLocaleString()}*`,
        '',
        `${language === 'en' ? 'View/Download Quotation' : 'কোটেশন দেখুন/ডাউনলোড'}:`,
        shareUrl,
      ];
      if (shop_phone) {
        lines.push('', `For queries, call: ${shop_phone}`);
      }
      if (frontend_url && shop_slug) {
        lines.push('', 'Visit us:', `${frontend_url}/${shop_slug}`);
      }
      lines.push('', `Thank you for choosing ${shop_name || 'us'}!`);

      const msg = encodeURIComponent(lines.filter(l => l != null).join('\n'));
      const waUrl = phone ? `https://wa.me/${phone.startsWith('880') ? phone : '880' + phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
      window.open(waUrl, '_blank');
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to generate share link' : 'শেয়ার লিংক তৈরি ব্যর্থ', variant: 'destructive' });
    } finally {
      setQuotationLoading(false);
    }
  };

  const downloadQuotationA4 = async () => {
    setQuotationLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/${id}/quotation-a4-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `quotation_${job?.job_number || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast({ title: language === 'en' ? 'A4 Quotation downloaded' : 'A4 কোটেশন ডাউনলোড হয়েছে' });
    } catch (error) {
      toast({ title: language === 'en' ? 'Failed to download' : 'ডাউনলোড ব্যর্থ', variant: 'destructive' });
    } finally {
      setQuotationLoading(false);
    }
  };

  const shareQuotationViaApps = async () => {
    if (!navigator.share) {
      toast({ title: language === 'en' ? 'Share not supported on this device' : 'এই ডিভাইসে শেয়ার সমর্থিত নয়', variant: 'destructive' });
      return;
    }
    setQuotationLoading(true);
    try {
      const shareRes = await axios.post(`${API}/jobs/${id}/quotation-share-link`);
      const shareUrl = shareRes.data.share_url;
      await navigator.share({
        title: `${language === 'en' ? 'Quotation' : 'কোটেশন'} - ${job?.job_number}`,
        text: `${language === 'en' ? 'Quotation for' : 'কোটেশন'} ${job?.customer_name} - Tk ${job?.total_amount}`,
        url: shareUrl,
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        toast({ title: language === 'en' ? 'Share failed' : 'শেয়ার ব্যর্থ', variant: 'destructive' });
      }
    } finally {
      setQuotationLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Spinner size="lg" />
        </div>
      </AdminLayout>
    );
  }

  if (!job) return null;

  const canUpdateStatus = hasRole(['owner', 'manager', 'front_desk']) || 
    (user?.role === 'technician' && job.technician_id === user?.id);

  // Parts-only job: has parts but no services and no custom services
  const hasServices = (job.services?.length > 0) || (job.custom_services?.length > 0);
  const isPartsOnly = !hasServices && (job.parts_used?.length > 0);

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(`${basePath}/jobs`)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold font-['Barlow_Condensed']">{job.job_number}</h1>
                <Badge className={`${statusColors[job.status]} border text-sm px-3 py-1`}>
                  {statusLabels[language][job.status]}
                </Badge>
              </div>
              <p className="text-slate-500">{formatDate(job.created_at, language)}</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            {job.status !== 'delivered' && (
              <Link to={`${basePath}/jobs/${id}/edit`}>
                <Button variant="outline" data-testid="edit-job-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Edit' : 'সম্পাদনা'}
                </Button>
              </Link>
            )}
            {/* Quotation Dropdown - hidden when invoice exists */}
            {!hasInvoice && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="text-blue-700 border-blue-300 hover:bg-blue-50" disabled={quotationLoading}>
                    {quotationLoading ? <Spinner size="sm" /> : <Printer className="w-4 h-4 mr-2" />}
                    {language === 'en' ? 'Quotation' : 'কোটেশন'}
                    <ChevronDown className="w-3 h-3 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isPremium ? (
                  <>
                  <DropdownMenuItem onClick={printQuotation} className="cursor-pointer">
                    <Printer className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Print Quotation' : 'কোটেশন প্রিন্ট'}
                  </DropdownMenuItem>
                  <div className="px-2 py-1.5 flex items-center gap-2" onClick={e => e.stopPropagation()}>
                    <Settings2 className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs text-slate-500">{language === 'en' ? 'Paper:' : 'পেপার:'}</span>
                    <div className="flex gap-1 ml-auto">
                      <button
                        className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                          thermalPaperSize === 58 ? 'bg-blue-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        onClick={async () => {
                          setThermalPaperSize(58);
                          try { await axios.put(`${API}/settings/garage`, { thermal_paper_size: 58 }); } catch {}
                        }}
                      >58mm</button>
                      <button
                        className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${
                          thermalPaperSize === 80 ? 'bg-blue-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        onClick={async () => {
                          setThermalPaperSize(80);
                          try { await axios.put(`${API}/settings/garage`, { thermal_paper_size: 80 }); } catch {}
                        }}
                      >80mm</button>
                    </div>
                  </div>
                  </>
                  ) : (
                    <div
                      className="px-2 py-2 flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded-sm"
                      onClick={() => setUpgradePopup({ open: true, feature: language === 'en' ? 'Thermal Print' : 'থার্মাল প্রিন্ট' })}
                    >
                      <Printer className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-400">{language === 'en' ? 'Thermal Print' : 'থার্মাল প্রিন্ট'}</span>
                      <PremiumBadge size="sm" className="ml-auto" />
                    </div>
                  )}
                  <DropdownMenuItem onClick={downloadQuotationA4} className="cursor-pointer">
                    <Download className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Download A4 PDF' : 'A4 PDF ডাউনলোড'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareQuotationWhatsApp} className="cursor-pointer">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Send via WhatsApp' : 'হোয়াটসঅ্যাপে পাঠান'}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={shareQuotationViaApps} className="cursor-pointer">
                    <Share2 className="w-4 h-4 mr-2" />
                    {language === 'en' ? 'Share via Apps' : 'অ্যাপের মাধ্যমে শেয়ার'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {hasInvoice && invoiceId && (
              <Link to={`${basePath}/invoices/${invoiceId}`}>
                <Button variant="outline" className="text-green-700 border-green-300 hover:bg-green-50" data-testid="view-invoice-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  {language === 'en' ? 'Invoice' : 'ইনভয়েস'}
                </Button>
              </Link>
            )}
            {(job.status === 'completed' || (isPartsOnly && !hasInvoice)) && (
              <Button onClick={createInvoice} className="bg-green-600 hover:bg-green-700" data-testid="create-invoice-btn">
                <FileText className="w-4 h-4 mr-2" />
                {t('create_invoice')}
              </Button>
            )}
            {hasRole(['owner', 'manager']) && (
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-red-600 border-red-300 hover:bg-red-50"
                data-testid="delete-job-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Status Timeline - hidden for parts-only jobs */}
        {!isPartsOnly && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">{language === 'en' ? 'Job Status' : 'জবের স্ট্যাটাস'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between overflow-x-auto pb-2">
                {statusFlow.map((status, index) => {
                  const isCurrent = job.status === status;
                  const isPast = statusFlow.indexOf(job.status) > index;
                  
                  return (
                    <React.Fragment key={status}>
                      <button
                        onClick={() => canUpdateStatus && updateStatus(status)}
                        disabled={!canUpdateStatus || updating}
                        className={`flex flex-col items-center min-w-[80px] ${canUpdateStatus ? 'cursor-pointer' : 'cursor-default'}`}
                        data-testid={`status-${status}`}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors ${
                          isCurrent ? 'bg-blue-600 text-white' :
                          isPast ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'
                        }`}>
                          {isPast ? <CheckCircle className="w-5 h-5" /> : index + 1}
                        </div>
                        <span className={`text-xs text-center ${isCurrent ? 'font-bold text-blue-600' : 'text-slate-500'}`}>
                          {statusLabels[language][status]}
                        </span>
                      </button>
                      {index < statusFlow.length - 1 && (
                        <div className={`flex-1 h-1 mx-2 ${isPast ? 'bg-green-500' : 'bg-slate-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              
              {canUpdateStatus && (
                <div className="mt-4 pt-4 border-t">
                  <label className="text-sm text-slate-500 mb-2 block">
                    {language === 'en' ? 'Quick Status Update:' : 'দ্রুত স্ট্যাটাস আপডেট:'}
                  </label>
                  <Select value={job.status} onValueChange={handleStatusChange} disabled={updating}>
                    <SelectTrigger className="w-full md:w-[250px]" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusFlow.filter(status => {
                        // Show 'delivered' for Quick Entry jobs OR if job already has an invoice
                        if (status === 'delivered') {
                          return job?.customer_name === 'Quick Entry' || hasInvoice;
                        }
                        return true;
                      }).map((status) => (
                        <SelectItem key={status} value={status}>
                          {statusLabels[language][status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('customer_details')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">{language === 'en' ? 'Name' : 'নাম'}</p>
                <p className="font-semibold">{job.customer_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{t('phone')}</p>
                <p className="font-semibold">{job.customer_phone}</p>
              </div>
              <Link to={`${basePath}/customers/${job.customer_id}`}>
                <Button variant="outline" size="sm" className="mt-2">
                  {language === 'en' ? 'View Customer' : 'গ্রাহক দেখুন'}
                </Button>
              </Link>
              {customerDues && customerDues.total_due > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg mt-3">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-700">
                        {language === 'en' ? 'Previous Due' : 'পূর্ববর্তী বকেয়া'}: {formatCurrency(customerDues.total_due, language)}
                      </p>
                      <p className="text-xs text-red-600">
                        {customerDues.total_invoices} {language === 'en' ? 'unpaid invoice(s)' : 'টি অপরিশোধিত ইনভয়েস'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Bike Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                {t('bike_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">{language === 'en' ? 'Make & Model' : 'ব্র্যান্ড ও মডেল'}</p>
                <p className="font-semibold">{job.bike_make || '-'} {job.bike_model || ''}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">{language === 'en' ? 'Registration' : 'রেজিস্ট্রেশন'}</p>
                <p className="font-semibold">{job.bike_registration || '-'}</p>
              </div>
              {job.technician_name && (
                <div>
                  <p className="text-sm text-slate-500">{t('technician')}</p>
                  <p className="font-semibold">{job.technician_name}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Services */}
        {(job.services?.length > 0 || job.custom_services?.length > 0) && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('services_selected')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {job.services.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium">{service.name}</span>
                    <span className="font-bold text-blue-800">{formatCurrency(service.price, language)}</span>
                  </div>
                ))}
                {job.custom_services?.map((cs, index) => (
                  <div key={`custom-${index}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">{cs.name}</span>
                      <span className="text-xs text-slate-400 ml-2">({language === 'en' ? 'Custom' : 'কাস্টম'})</span>
                    </div>
                    <span className="font-bold text-blue-800">{formatCurrency(cs.price, language)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg mt-4">
                  <span className="font-semibold">{language === 'en' ? 'Services Total' : 'সার্ভিস মোট'}</span>
                  <span className="font-bold text-blue-800">{formatCurrency(job.total_services, language)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Parts Used */}
        {(job.parts_used?.length > 0 || job.custom_parts?.length > 0) && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t('parts_used')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {job.parts_used?.map((part, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">{part.part_name}</span>
                      <span className="text-slate-500 ml-2">× {part.quantity}</span>
                    </div>
                    <span className="font-bold">{formatCurrency(part.quantity * part.unit_price, language)}</span>
                  </div>
                ))}
                {job.custom_parts?.map((cp, index) => (
                  <div key={`cp-${index}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <span className="font-medium">{cp.name}</span>
                      <span className="text-xs text-slate-400 ml-1">({language === 'en' ? 'Custom' : 'কাস্টম'})</span>
                      <span className="text-slate-500 ml-2">× {cp.quantity || 1}</span>
                    </div>
                    <span className="font-bold">{formatCurrency((cp.quantity || 1) * (cp.selling_price || 0), language)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between p-3 bg-orange-50 rounded-lg mt-4">
                  <span className="font-semibold">{language === 'en' ? 'Parts Total' : 'পার্টস মোট'}</span>
                  <span className="font-bold text-orange-600">{formatCurrency(job.total_parts, language)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes */}
        {job.notes && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>{t('notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 whitespace-pre-wrap">{job.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Total */}
        <Card className="mt-6 bg-slate-900 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400">{language === 'en' ? 'Total Amount' : 'মোট পরিমাণ'}</p>
                <p className="text-4xl font-bold">{formatCurrency(job.total_amount, language)}</p>
              </div>
              {hasInvoice && invoiceId ? (
                <Link to={`${basePath}/invoices/${invoiceId}`}>
                  <Button 
                    size="lg"
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="view-invoice-btn-2"
                  >
                    <FileText className="w-5 h-5 mr-2" />
                    {language === 'en' ? 'View Invoice' : 'ইনভয়েস দেখুন'}
                  </Button>
                </Link>
              ) : (job.status === 'completed' || (isPartsOnly && !hasInvoice)) ? (
                <Button 
                  onClick={createInvoice}
                  size="lg"
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="create-invoice-btn-2"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  {t('create_invoice')}
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>

        {/* Delete Job Confirmation Dialog */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-500" />
                {language === 'en' ? 'Delete Job?' : 'জব মুছে ফেলুন?'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {language === 'en'
                  ? `This will permanently delete job ${job.job_number}${hasInvoice ? ' and its associated invoice' : ''}, restore any parts stock, and adjust customer records. This action cannot be undone.`
                  : `${job.job_number} জব${hasInvoice ? ' এবং সংশ্লিষ্ট ইনভয়েস' : ''} স্থায়ীভাবে মুছে যাবে, পার্টস স্টক ফেরত আসবে এবং গ্রাহক রেকর্ড সমন্বয় হবে। এর পরে ফেরত যাবে না।`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{language === 'en' ? 'Cancel' : 'বাতিল'}</AlertDialogCancel>
              <AlertDialogAction
                onClick={deleteJob}
                className="bg-red-600 hover:bg-red-700"
                disabled={deletingJob}
              >
                {deletingJob ? <Spinner size="sm" /> : (language === 'en' ? 'Delete' : 'মুছে ফেলুন')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Status Backwards Confirmation Dialog */}
        <AlertDialog open={showStatusConfirm} onOpenChange={setShowStatusConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                {language === 'en' ? 'Confirm Status Change' : 'স্ট্যাটাস পরিবর্তন নিশ্চিত করুন'}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {language === 'en' 
                  ? 'You are moving the job status backwards. Are you sure you want to proceed?' 
                  : 'আপনি জবের স্ট্যাটাস পিছনে নিয়ে যাচ্ছেন। আপনি কি নিশ্চিত যে আপনি এগিয়ে যেতে চান?'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={cancelStatusChange}>
                {language === 'en' ? 'Cancel' : 'বাতিল'}
              </AlertDialogCancel>
              <AlertDialogAction onClick={confirmStatusChange}>
                {language === 'en' ? 'Confirm' : 'নিশ্চিত'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <PremiumUpgradeDialog
        open={upgradePopup.open}
        onOpenChange={(v) => setUpgradePopup(prev => ({ ...prev, open: v }))}
        feature={upgradePopup.feature}
      />
    </AdminLayout>
  );
}
